<template>
  <div class="h-full">
    <!-- Location Bar -->
    <div class="flex justify-center items-center h-10 w-full bg-blue-400 text-white">
      <i class="material-icons text-blue-500">location_on</i>
      <span class="mx-24">{{ address }}</span>
      <span
        class="cursor-pointer underline"
        @click="showEditor"
      >
        Edit
      </span>
    </div>

    <div class="map h-full relative">
      <!-- Search Input -->
      <div
        class="flex absolute left-0 right-0 bg-gray-500 text-white w-96 mx-auto mt-12 py-3 px-4 z-50 opacity-90"
        v-if="showLocationEditor"
      >
        <i class="material-icons text-blue-400">search</i>
        <input
          ref="searchInput"
          v-model="searchText"
          class="bg-transparent flex-grow outline-none ml-4"
          placeholder="Search"
          @keyup="nearbySearch"
        />
      </div>

      <!-- Locations Box -->
      <div
        class="absolute left-0 right-0 mx-auto mt-24 w-96 text-white text-center opacity-90 z-50"
        v-if="locations.length"
      >
        <div
          class="w-96 py-3 px-4 mt-1 cursor-pointer bg-gray-500 hover:bg-blue-400"
          v-for="location in locations"
          :key="location.place_id"
          @click="openFromLocation(location)"
        >
          <span>{{ location.description }}</span>
        </div>
      </div>

      <!-- Current Location -->
      <div
        class="flex justify-between absolute left-0 bottom-0 right-0 bg-gray-500 text-white w-64 mx-auto mb-12 py-3 px-4 z-50 opacity-90 cursor-pointer"
        @click="forceCurrentLocation"
      >
        Current Location
        <i class="material-icons text-blue-400">my_location</i>
      </div>

      <!-- View Location -->
      <div
        class="text-center absolute left-0 right-0 ml-auto bg-white w-96 h-full z-50 overflow-y-auto"
        v-if="infoModal"
      >
        <div class="flex items-center justify-center h-full" v-if="loading">
          <bar-loader color="#4db1fa" :width="320" />
        </div>

        <div class="relative" v-if="!loading">
          <i
            class="material-icons absolute left-0 ml-4 mt-4 cursor-pointer"
            :class="(selectedLocation.photos || []).length ? 'text-white' : 'text-blue-400'"
            @click="closeInfoModal"
          >close</i>

          <img
            class="info-image w-full"
            :src="(selectedLocation.photos || []).length ? selectedLocation.photos[0].getUrl() : '/images/map/no-image.png'"
          />

          <p class="text-xl my-6">{{ selectedLocation.name }}</p>

          <p class="text-5xl font-bold mb-1">{{ selectedLocation.rating || '0' }}</p>

          <star-rating
            class="justify-center mb-12"
            :rating="selectedLocation.rating"
            :star-size="30"
            :show-rating="false"
            active-color="#4db1fa"
            read-only
          />

          <div class="w-full flex px-20">
            <div class="w-1/4 text-left flex flex-col">
              <i class="material-icons text-blue-400 h-12">place</i>
              <i
                class="material-icons text-blue-400 h-12 mt-4"
                v-if="selectedLocation.website"
              >language</i>
              <i
                class="material-icons text-blue-400 h-12 mt-4"
                v-if="selectedLocation.international_phone_number"
              >phone</i>
            </div>
            <div class="w-3/4 text-right">
              <p class="h-12 break-all overflow-hidden">
                <a
                  :href="`https://www.google.com/maps/place/?q=place_id:${selectedLocation.place_id}`"
                  target="_blank"
                >
                  {{ selectedLocation.vicinity }}
                </a>
              </p>
              <p
                class="h-12 break-all mt-4 overflow-hidden"
                v-if="selectedLocation.website"
              >
                <a :href="selectedLocation.website" target="_blank">
                  {{ selectedLocation.website }}
                </a>
              </p>
              <p
                class="h-12 break-all mt-4 overflow-hidden"
                v-if="selectedLocation.international_phone_number"
              >
                <a :href="`tel:${selectedLocation.international_phone_number.replace(/ /g, '').replace(/-/g, '')}`">
                  {{ selectedLocation.international_phone_number }}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="h-full" ref="mapContainer"></div>
    </div>

    <base-footer />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import MarkerClusterer from "@google/markerclusterer";
import LocationService from '@/services/core/LocationService';

export default {
  data() {
    return {
      searchText: "",
      locations: [],
      selectedLocation: {},
      infoModal: false,
      loading: false,
      showLocationEditor: false,
    };
  },
  computed: {
    ...mapState("profile", ["location", "address", "isCurrentLocation"])
  },
  watch: {
    isCurrentLocation() {
      this.currentLocation();
    }
  },
  created() {
    if (this.$route.params.showLocationEditor) {
      this.showEditor();
    }
  },
  methods: {
    ...mapActions('profile', ['setLocation', 'setAddress']),

    showEditor() {
      this.showLocationEditor = true;
      this.$nextTick(() => {
        this.$refs.searchInput.focus();
      });
    },

    currentLocation() {
      if (this.isCurrentLocation && this.map) {
        this.map.setCenter(this.location);
        this.map.setZoom(14);
      }
    },

    async forceCurrentLocation() {
      const { address, location } = await LocationService.initialize();

      this.setAddress(address);
      this.setLocation(location);

      this.currentLocation();
    },

    loadNearby() {
      const service = new google.maps.places.PlacesService(this.map);
      const center = this.map.getCenter();

      service.nearbySearch(
        {
          location: new google.maps.LatLng(center.lat(), center.lng()),
          radius: "15000",
          type: ["police"]
        },
        results => {
          const markers = (results || []).map(place => {
            const marker = new google.maps.Marker({
              position: place.geometry.location,
              icon: "/images/map/police.png"
            });

            marker.addListener("click", () => this.showInfo(place));

            return marker;
          });

          if (this.cluster) {
            this.cluster.clearMarkers();
            this.cluster.addMarkers(markers);
          } else {
            this.cluster = new MarkerClusterer(this.map, markers, {
              imagePath:
                "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m"
            });
          }
        }
      );
    },

    nearbySearch() {
      if (this.searchText.length < 3) {
        this.locations = [];
        return;
      }

      const service = new google.maps.places.AutocompleteService();

      service.getPlacePredictions({
        input: this.searchText,
        types: ['(cities)'],
      }, predictions => {
        this.locations = (predictions || []).slice(0, 5);
      });
    },

    openFromLocation(location) {
      const service = new google.maps.places.PlacesService(this.map);
      service.getDetails({ placeId: location.place_id }, info => {
        const city = LocationService.findAddress(info, 'locality');
        const state = LocationService.findAddress(info, 'administrative_area_level_1');

        this.setAddress(`${city}, ${state}`);
        this.setLocation(info.geometry.location);
        this.currentLocation();

        this.searchText = '';
        this.locations = [];
        this.showLocationEditor = false;
      });
    },

    showInfo(place) {
      this.loading = true;
      this.infoModal = true;

      // Get details
      const service = new google.maps.places.PlacesService(this.map);
      service.getDetails({ placeId: place.place_id }, info => {
        this.selectedLocation = info;
        this.loading = false;
      });
    },

    closeInfoModal() {
      this.infoModal = false;
      this.selectedLocation = {};
    }
  },
  async mounted() {
    this.map = new google.maps.Map(this.$refs.mapContainer, {
      center: this.location,
      zoom: 14,
      styles: [
        {
          elementType: "geometry",
          stylers: [
            {
              color: "#212121"
            }
          ]
        },
        {
          elementType: "labels.icon",
          stylers: [
            {
              visibility: "off"
            }
          ]
        },
        {
          elementType: "labels.text.fill",
          stylers: [
            {
              color: "#757575"
            }
          ]
        },
        {
          elementType: "labels.text.stroke",
          stylers: [
            {
              color: "#212121"
            }
          ]
        },
        {
          featureType: "administrative",
          elementType: "geometry",
          stylers: [
            {
              color: "#757575"
            }
          ]
        },
        {
          featureType: "administrative.country",
          elementType: "labels.text.fill",
          stylers: [
            {
              color: "#9e9e9e"
            }
          ]
        },
        {
          featureType: "administrative.land_parcel",
          stylers: [
            {
              visibility: "off"
            }
          ]
        },
        {
          featureType: "administrative.locality",
          elementType: "labels.text.fill",
          stylers: [
            {
              color: "#bdbdbd"
            }
          ]
        },
        {
          featureType: "poi",
          elementType: "labels.text.fill",
          stylers: [
            {
              color: "#757575"
            }
          ]
        },
        {
          featureType: "poi.park",
          elementType: "geometry",
          stylers: [
            {
              color: "#181818"
            }
          ]
        },
        {
          featureType: "poi.park",
          elementType: "labels.text.fill",
          stylers: [
            {
              color: "#616161"
            }
          ]
        },
        {
          featureType: "poi.park",
          elementType: "labels.text.stroke",
          stylers: [
            {
              color: "#1b1b1b"
            }
          ]
        },
        {
          featureType: "road",
          elementType: "geometry.fill",
          stylers: [
            {
              color: "#2c2c2c"
            }
          ]
        },
        {
          featureType: "road",
          elementType: "labels.text.fill",
          stylers: [
            {
              color: "#8a8a8a"
            }
          ]
        },
        {
          featureType: "road.arterial",
          elementType: "geometry",
          stylers: [
            {
              color: "#373737"
            }
          ]
        },
        {
          featureType: "road.highway",
          elementType: "geometry",
          stylers: [
            {
              color: "#3c3c3c"
            }
          ]
        },
        {
          featureType: "road.highway.controlled_access",
          elementType: "geometry",
          stylers: [
            {
              color: "#4e4e4e"
            }
          ]
        },
        {
          featureType: "road.local",
          elementType: "labels.text.fill",
          stylers: [
            {
              color: "#616161"
            }
          ]
        },
        {
          featureType: "transit",
          elementType: "labels.text.fill",
          stylers: [
            {
              color: "#757575"
            }
          ]
        },
        {
          featureType: "water",
          elementType: "geometry",
          stylers: [
            {
              color: "#000000"
            }
          ]
        },
        {
          featureType: "water",
          elementType: "labels.text.fill",
          stylers: [
            {
              color: "#3d3d3d"
            }
          ]
        }
      ]
    });

    this.map.addListener("idle", this.loadNearby);

    // Prevent re-center same location
    if (!this.isCurrentLocation) {
      this.currentLocation();
    }
  }
};
</script>

<style scoped>
.map {
  height: calc(100% - 104px);
}

.info-image {
  max-height: 217px;
}
</style>
